<template lang="html">
  <div>
    <!-- <v-btn color="primary" @click="() => {window = window + 1; $vuetify.goTo(0, {duration: 0})}">text</v-btn> -->
    <v-window
      v-model="window"
      touchless
      class="mb-4"
    >
      <v-window-item>
        <v-container
          grid-list-md
          py-8
        >
          <v-layout
            wrap
            justify-center
          >
            <v-flex v-bind="_pageLayout">
              <v-layout wrap>
                <v-flex class="display-2 font-weight-light primary--text">
                  {{ variantInfo.name }}
                </v-flex>
                <v-flex
                  xs12
                  v-html="_formatLineBreaks(variantInfo.description)"
                />

                <v-flex
                  v-if="!loading"
                  xs12
                  pa-0
                >
                  <template v-for="panel in helpPanels">
                    <div :key="panel.id">
                      <v-flex
                        xs12
                        py-3
                        class="cursor-pointer"
                        @click="togglePanel(panel.id)"
                      >
                        <v-layout
                          align-center
                          ma-0
                        >
                          <v-icon
                            :color="panel.color"
                            left
                          >
                            {{ panel.icon }}
                          </v-icon>
                          <div :class="[panel.textClass]">
                            {{ panel.title }}
                          </div>
                          <v-spacer />
                          <v-icon v-if="activePanels[panel.id]">
                            close
                          </v-icon>
                        </v-layout>
                      </v-flex>
                      <v-expand-transition>
                        <div v-if="activePanels[panel.id]">
                          <v-container
                            fluid
                            pt-0
                            px-2
                          >
                            <v-layout
                              wrap
                              ma-0
                            >
                              <v-flex
                                xs12
                                pa-0
                                v-html="panel.body"
                              />
                              <v-flex
                                v-if="panel.buttonTo"
                                pa-0
                                xs12
                              >
                                <v-btn
                                  color="primary"
                                  class="mt-3"
                                  :to="panel.buttonTo"
                                >
                                  {{ panel.buttonText || 'Get Started' }}
                                </v-btn>
                              </v-flex>
                            </v-layout>
                          </v-container>
                        </div>
                      </v-expand-transition>
                    </div>
                  </template>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>

        <v-divider v-if="!loading && !loadingTest" />
        <v-progress-linear
          :indeterminate="true"
          height="2"
          class="ma-0"
          :active="loading || loadingTest"
        />
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex v-bind="_pageLayout">
              <v-layout wrap>
                <v-flex xs12>
                  <CalculatorCalculator
                    :calculator="calculator"
                    :calculation-data.sync="calculationData"
                    :report.sync="report"
                    :calculator-info="calcInfo"
                    :can-have-assistance="variantInfo.canHaveAssistance"
                    :assisted="assisted"
                    :test="isTest"
                    @calculate="calculate"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
      </v-window-item>
      <v-window-item>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex v-bind="_pageLayout">
              <v-layout wrap>
                <v-flex xs12>
                  <SiCalculationLoader
                    v-if="$vuetify.breakpoint.xsOnly"
                    :value="calculationComplete"
                    :commenced="calculationCommenced"
                    class="my-4"
                    :error-path="`calculations/${calculatorId}/${calculationId}/error`"
                  />
                </v-flex>
                <v-flex
                  mt-4
                  mb-3
                  px-0
                >
                  <span class="headline font-weight-light">Payment</span>
                  <v-divider />
                </v-flex>
                <v-flex xs12>
                  <SiCheckout
                    :skus="[calculatorSku]"
                    :register-paths="[`calculations/${calculatorId}/${calculationId}`]"
                    :register-paths-db="[]"
                    :charge-description="`${(variantInfo.product || {}).receiptText || ''}`"
                    :disabled="!calculationComplete"
                    :transaction-id="calculationId"
                    :metadata="metadata"
                    :loading="checkoutLoading"
                    allow-client-reference
                    allow-invoicing
                    @finalised="finaliseCheckout"
                  >
                    <SiCalculationLoader
                      v-if="$vuetify.breakpoint.smAndUp"
                      :value="calculationComplete"
                      :commenced="calculationCommenced"
                      class="my-4"
                      :error-path="`calculations/${calculatorId}/${calculationId}/error`"
                    />
                  </SiCheckout>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
      </v-window-item>
    </v-window>

    <v-snackbar
      :value="assisted"
      bottom
      color="success"
      :timeout="-1"
    >
      <v-flex text-center>
        Assisted calculation for:
        <div v-if="$route.query.name">
          <strong>{{ $route.query.name }} - {{ $route.query.calculationId }}</strong>
        </div>
      </v-flex>
    </v-snackbar>

    <SiLoaderPopup
      :value="(isTest || assisted) && calculationCommenced"
      :label="popupText"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { rtdb } from '@/plugins/firebase'
import uniqid from 'uniqid'
import links from '@/mixins/links'
import helpers from '@/mixins/helpers'
import binder from '@/mixins/binder'
import clientPath from '@/plugins/client-path'
import axios from 'axios'

export default {
  mixins: [ helpers, binder, links ],
  data () {
    return {
      window: 0,
      loading: true,
      loadingTest: false,
      assisted: false,
      assistanceId: '',
      calcInfo: {},
      calculator: {},
      calculationId: uniqid(),
      calculationData: {},
      report: {},
      calculatorDetails: {},
      calculationCommenced: false,
      calculationComplete: false,
      checkoutComplete: false,
      checkoutLoading: false,
      activePanels: {},
      popupText: 'Calculating...'
    }
  },
  computed: {
    ...mapGetters(['_calculators', '_pageLayout', '_userDevice', '_userLocation', '_user']),
    calculatorSku () {
      return `${this.calculatorId}_${this.variantId}`
    },
    calculatorId () {
      return this.$route.params.calculator
    },
    variantId () {
      return this.$route.params.variant
    },
    variantInfo () {
      return (this.calcInfo.variants || {})[this.variantId] || { product: {} }
    },
    helpPanels () {
      var panels = []
      let help = {
        id: 'help',
        icon: 'info_outline',
        color: 'info',
        textClass: 'info--text',
        title: 'Getting started',
        body: this.variantInfo.gettingStartedText
      }
      let assistance = {
        id: 'assistance',
        icon: 'support_agent',
        title: 'Human assistance',
        color: 'success',
        textClass: 'success--text',
        body: this.variantInfo.assistanceText,
        buttonTo: `/assistance?calculator=${this.calculatorId}&variant=${this.variantId}`,
        buttonText: 'Get Assistance'
      }
      if (this.variantInfo.gettingStartedText) panels.push(help)
      if (this.variantInfo.canHaveAssistance) panels.push(assistance)

      return panels
    },
    metadata () {
      let { calculatorId, calculationId } = this
      return { type: 'Calculation', calculatorId, calculationId }
    },
    isTest () {
      return !!((this.$route.query.testId || this.$route.query.calculationId) && this.$route.query.token)
    }
  },
  watch: {
    '$route.params.variant' () {
      this.bindCalculator()
      this.calculatorDetails = {}
    },
    calculationComplete () {
      if (this.checkoutComplete) this.gotoReport()
    },
    checkoutComplete () {
      if (this.calculationComplete) this.gotoReport()
    }
  },
  beforeMount () {
    let { assistanceId, calculationId } = this.$route.query
    // check if there is a variant. Variants are new in 2.0+ so that previous links work default to 'standard'
    if (!this.$route.params.variant) {
      this.$router.replace({ path: `${this.$route.path}/standard`, query: this.$route.query })
    }

    if (assistanceId) {
      this.assistanceId = assistanceId
      this.assisted = true
      this.calculationId = calculationId || uniqid()
    }

    this.calculationData._variant = this.variantId
    this.calculationData._sku = this.calculatorSku

    this.calcInfo = this._calculators[this.calculatorId] || {}

    if (this.isTest) this.loadTestData()

    if (this.$route.params.variant) this.bindCalculator()
  },
  mounted () {
    clientPath.track(`open_calc_${this.calculatorId}`)

    rtdb.ref(`calculators/${this.calculatorId}/report/activeVersionId`).once('value', s => {
      this.calculatorDetails.reportVersion = s.val()
    })
    this.$store.commit('set_loader', false)
  },
  methods: {
    async loadTestData () {
      this.loadingTest = true
      let { query: { testId, calculationId, token }, params: { calculator } } = this.$route
      let url = testId 
          ? `${process.env.VUE_APP_API_URL}/calculators/${calculator}/tests/${testId}/data` 
          : `${process.env.VUE_APP_API_URL}/calculators/${calculator}/${calculationId}/data` 
      
      let { data } = await axios({ url, method: 'GET', headers: { Authorization: `Bearer ${token}` }})

      if (data) this.calculationData = data    
      this.loadingTest = false
    },
    togglePanel (panelId) {
      this.$set(this.activePanels, panelId, !this.activePanels[panelId])
    },
    bindCalculator () {
      const callback = (v) => {
        this.calcInfo = v
        this.calculatorDetails.restricted = v.restricted
      }
      this.$bindRtdb('calculator', `calculators/${this.calculatorId}/structure`,{ loader: 'loading' })
      this.$bindRtdb('calcInfo', `calculators/_details/${this.calculatorId}`, { callback } )
      // this.$unbindAll()
      // this.$bindxxx(rtdb.ref(`calculators/${this.calculatorId}/structure`), 'calculator', 'loading')
      // this.$bindxxx(rtdb.ref(`calculators/_details/${this.calculatorId}`), v => {
      //   this.calcInfo = v
      //   this.calculatorDetails.restricted = v.restricted
      // })
    },
    calculate (data) {
      clientPath.track(`run_calc_${this.calculatorId}`, { type: 'run_calculation', calculatorId: this.calculatorId, calculationId: this.calculationId })
      clientPath.tag(this.calculatorSku)
      clientPath.tag(data.calculationData.super_type)

      let timestamp = new Date().toISOString()
      let updates = {}
      let calcPath = `calculations/${this.calculatorId}/${this.calculationId}`
      let details = {
        timestamp: timestamp,
        calculatorId: this.calculatorId,
        variant: this.variantId,
        calculationId: this.calculationId,
        reportVersion: this.calculatorDetails.reportVersion
      }

      updates[`${calcPath}/calculationData`] = data.calculationData
      updates[`${calcPath}/_calculated`] = false
      updates[`${calcPath}/_variant`] = this.variantId
      updates[`${calcPath}/_device`] = this._userDevice
      updates[`${calcPath}/_location`] = this._userLocation
      updates[`${calcPath}/details`] = this._removeUndefined(details)
      updates[`${calcPath}/timestamp`] = timestamp
      updates[`${calcPath}/report`] = data.report
      updates[`${calcPath}/_uid`] = this._user.uid

      rtdb.ref().update(updates)

      this.calculationCommenced = true
      this.window = 1

      rtdb.ref(`calculations/${this.calculatorId}/${this.calculationId}/_calculated`).on('value', s => {
        if (s.val()) {
          this.calculationComplete = true
          if (this.assisted) {
            this.popupText = 'Window will close...'
            setTimeout(() => { window.top.close() }, 1000)
          }
          if (this.isTest) {
            this.$router.push(`/${this._viewReportPath(this.calculatorId, this.calculationId, this.$route.query.token)}`)
          }
        }
      })
    },
    finaliseCheckout (client) {
      this.checkoutLoading = true
      
      clientPath.track(`purchase_calc_${this.calculatorId}`, { type: 'purchase_calculation', calculatorId: this.calculatorId, calculationId: this.calculationId })
      
      rtdb.ref(`calculations/${this.calculatorId}/${this.calculationId}/client`)
        .set(client)
        .then(() => this.checkoutComplete = true)
    },
    gotoReport () {
      this.$router.push(`/view/${this.calculatorId}/${this.calculationId}?token=${this.calculationId}`)
    }
  }
}
</script>

<style lang="css" scoped>

</style>
